import { ChainIdEnum } from "../../enums/chain";
import { CHAIN_CONFIGS } from "./configs";
import { getChainIdFromEnv } from "./getters";
import { AppBranding, AppBrandingLinks } from "./types";

// =============================================================================
// App Branding helpers
// =============================================================================

/**
 * Get app branding for a chain
 */
export const getAppBranding = (chainId?: ChainIdEnum): AppBranding => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return (
    config?.branding ?? {
      appName: "MarginX Exchange",
      shortName: "MarginX",
      description:
        "High-performance decentralized exchange for spot and perpetual trading.",
      keywords: ["marginx", "defi", "perpetuals", "trading"],
      domain: "marginx.trade",
      themeColor: "#ffffff",
      backgroundColor: "#000000",
      logoFolder: "marginx",
      links: {
        home: "https://app.marginx.trade",
        docs: "https://docs.marginx.trade",
        twitter: "https://x.com/marginx_trade",
        discord: "https://discord.com/invite/marginx",
        github: "https://github.com/procyon-labs-io",
        telegram: "https://t.me/marginx_trade",
        termsOfService: "https://marginx.trade/terms",
      },
    }
  );
};

/**
 * Get app name for a chain
 */
export const getAppName = (chainId?: ChainIdEnum): string => {
  return getAppBranding(chainId).appName;
};

/**
 * Get app short name for a chain
 */
export const getAppShortName = (chainId?: ChainIdEnum): string => {
  return getAppBranding(chainId).shortName;
};

/**
 * Get app description for a chain
 */
export const getAppDescription = (chainId?: ChainIdEnum): string => {
  return getAppBranding(chainId).description;
};

/**
 * Get app keywords for a chain
 */
export const getAppKeywords = (chainId?: ChainIdEnum): string[] => {
  return getAppBranding(chainId).keywords;
};

/**
 * Get app domain for a chain
 */
export const getAppDomain = (chainId?: ChainIdEnum): string => {
  return getAppBranding(chainId).domain;
};

/**
 * Get app theme color for a chain
 */
export const getAppThemeColor = (chainId?: ChainIdEnum): string => {
  return getAppBranding(chainId).themeColor;
};

/**
 * Get app background color for a chain
 */
export const getAppBackgroundColor = (chainId?: ChainIdEnum): string => {
  return getAppBranding(chainId).backgroundColor;
};

/**
 * Get logo folder for a chain
 */
export const getLogoFolder = (chainId?: ChainIdEnum): string => {
  return getAppBranding(chainId).logoFolder;
};

/**
 * Get app links for a chain
 */
export const getAppLinks = (chainId?: ChainIdEnum): AppBrandingLinks => {
  return getAppBranding(chainId).links;
};

// ============================================
// BRAND ASSET URLS
// ============================================
//
// Single source of truth for per-brand image URLs. Every brand ships the
// same set of files under `public/assets/logo/<brand>/`; these helpers build
// the URLs so the folder convention and filenames live in exactly ONE place.
// Prefer the named getters (getBrandIconUrl, etc.) at call sites.

/** Canonical brand asset filenames shipped in every `public/assets/logo/<brand>/`. */
export const BRAND_ASSET = {
  /** Circular brand icon — header, toasts, notification icons. */
  icon: "logo_icon.png",
  /** Horizontal wordmark / text lockup. */
  wordmark: "logo_text_white.svg",
  /** Square favicon (PNG, 512). */
  favicon: "favicon.png",
  /** Square favicon (SVG). */
  faviconSvg: "favicon.svg",
  /** Legacy .ico favicon (multi-size). */
  faviconIco: "favicon.ico",
  /** PWA maskable icon. */
  favicon196: "favicon-196.png",
  /** PWA icon. */
  favicon192: "favicon-192.png",
  /** Open Graph / social share image (1200x630). */
  ogImage: "og-image.png",
} as const;

export type BrandAsset = (typeof BRAND_ASSET)[keyof typeof BRAND_ASSET];

/** Public base path for a brand's logo assets, e.g. `/assets/logo/marginx`. */
export const getBrandAssetBasePath = (chainId?: ChainIdEnum): string =>
  `/assets/logo/${getLogoFolder(chainId)}`;

/**
 * Resolve a brand asset URL for the active (or given) chain. Prefer the named
 * helpers below; use this directly only for one-off assets.
 */
export const getBrandAssetUrl = (
  asset: BrandAsset,
  chainId?: ChainIdEnum
): string => `${getBrandAssetBasePath(chainId)}/${asset}`;

/** Circular brand icon URL (header, toasts, notification icons). */
export const getBrandIconUrl = (chainId?: ChainIdEnum): string =>
  getBrandAssetUrl(BRAND_ASSET.icon, chainId);

/** Wordmark / text-logo URL. */
export const getBrandWordmarkUrl = (chainId?: ChainIdEnum): string =>
  getBrandAssetUrl(BRAND_ASSET.wordmark, chainId);

/** Open Graph / social-share image URL. */
export const getBrandOgImageUrl = (chainId?: ChainIdEnum): string =>
  getBrandAssetUrl(BRAND_ASSET.ogImage, chainId);

/** Favicon URL — PNG by default, SVG when `svg` is true. */
export const getBrandFaviconUrl = (opts?: {
  svg?: boolean;
  chainId?: ChainIdEnum;
}): string =>
  getBrandAssetUrl(
    opts?.svg ? BRAND_ASSET.faviconSvg : BRAND_ASSET.favicon,
    opts?.chainId
  );

/**
 * Get specific app link
 */
export const getAppLink = (
  key: keyof AppBrandingLinks,
  chainId?: ChainIdEnum
): string | undefined => {
  return getAppLinks(chainId)[key];
};
